import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PencilSimple, Trash, Plus, FileImage } from '@phosphor-icons/react';
import { useErrorLogging, ERROR_SEVERITY, ERROR_CATEGORY } from '../../../../utils/errorLogging';
import { useApp } from '../../../../contexts/AppContext';
import { useData } from '../../../../hooks/useData';
import { TABLES, MODAL_TYPES } from '../../../../constants';
import { LoadingSpinner } from '../../../../utils/LoadingSpinner';
import pageStyles from '../../../../styles/pages.module.css';
import tableStyles from '../../../../styles/tables.module.css';

// Constants
const ALL_FLOORS_ID = '__all_floors__';
const ALL_FLOORS_OPTION = { id: ALL_FLOORS_ID, name: 'כל הקומות' };

// Pure function for base floor name formatting (for selector tabs)
export const formatBaseFloorName = floorNum => {
  if (floorNum === 0) return 'קומת קרקע';
  if (floorNum < 0) return 'קומת מרתף';

  // Hebrew ordinal numbers for floors
  const hebrewOrdinals = [
    'ראשונה',
    'שנייה',
    'שלישית',
    'רביעית',
    'חמישית',
    'שישית',
    'שביעית',
    'שמינית',
    'תשיעית',
    'עשירית',
  ];

  return floorNum <= 10 ? `קומה ${hebrewOrdinals[floorNum - 1]}` : `קומה ${floorNum}`;
};

// Main component
const SpacesStructure = ({ headerOverride }) => {
  const { currentProject, openModal } = useApp();
  const { logError } = useErrorLogging();
  const [selectedFloor, setSelectedFloor] = useState(null);

  // Data fetching with proper conditions and error handling
  const {
    data: allFloors,
    isLoading: floorsLoading,
    error: floorsError,
    remove: removeFloor,
  } = useData(TABLES.PROJECT_FLOORS, {
    enabled: Boolean(currentProject?.id),
    realtime: true,
    select: '*',
    staleTime: 5000,
    filter: { project_id: currentProject?.id },
    onError: error => {
      logError(error, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_FETCH,
        message: `Error fetching floors for project ${currentProject?.id}`,
      });
    },
    onSuccess: data => {
      console.log('Received floors:', data);
    },
  });

  // Get floor IDs for filtering spaces (project_spaces links via floor_id, not project_id)
  const floorIds = useMemo(() => allFloors?.map(f => f.id) || [], [allFloors]);

  const {
    data: allSpaces,
    isLoading: spacesLoading,
    softDeleteSpace,
  } = useData(TABLES.PROJECT_SPACES, {
    enabled: Boolean(currentProject?.id) && floorIds.length > 0,
    select: `*, project_floors!inner(*)`,
    filter: { floor_id: floorIds },
    realtime: true,
    staleTime: 5000,
    onError: error => {
      logError(error, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_FETCH,
        message: 'Error fetching spaces from project',
      });
    },
  });

  // Get space IDs for filtering windows
  const spaceIds = useMemo(() => allSpaces?.map(s => s.id) || [], [allSpaces]);

  const { data: allWindows, isLoading: windowsLoading } = useData(TABLES.PROJECT_WINDOWS, {
    enabled: Boolean(currentProject?.id) && spaceIds.length > 0,
    realtime: true,
    select: '*',
    filter: { space_id: spaceIds },
    staleTime: 5000,
  });

  const {
    data: spaceTypes,
    isLoading: spaceTypesLoading,
    error: spaceTypesError,
  } = useData(TABLES.MASTER_SPACES, {
    enabled: Boolean(currentProject?.id),
    realtime: true,
    select: '*',
  });

  // Fetch floor-level defaults (for floor choice badges)
  const { data: floorDefaults } = useData(TABLES.PROJECT_DEFAULT_OPTIONS, {
    select: 'master_item_id, floor_id, selected_option',
    filter: { project_id: currentProject?.id },
    enabled: Boolean(currentProject?.id),
    realtime: true,
  });

  // Fetch master items for resolving choice child names
  const floorDefaultChildIds = useMemo(() => {
    if (!floorDefaults) return [];
    return [
      ...new Set(
        floorDefaults
          .filter(d => d.floor_id && d.selected_option?.selected_child_id)
          .map(d => d.selected_option.selected_child_id),
      ),
    ];
  }, [floorDefaults]);

  const { data: choiceChildItems } = useData(TABLES.MASTER_ITEMS, {
    select: 'id, item_name',
    filter: { id: floorDefaultChildIds },
    enabled: floorDefaultChildIds.length > 0,
    realtime: false,
  });

  // Build lookup: floorId → [{ itemName, choiceName }]
  const floorChoiceBadges = useMemo(() => {
    if (!floorDefaults || !choiceChildItems) return {};
    const childMap = new Map(choiceChildItems.map(c => [c.id, c.item_name]));
    const badges = {};
    floorDefaults.forEach(d => {
      if (!d.floor_id || !d.selected_option?.selected_child_id) return;
      const childName = childMap.get(d.selected_option.selected_child_id);
      if (!childName) return;
      if (!badges[d.floor_id]) badges[d.floor_id] = [];
      badges[d.floor_id].push(childName);
    });
    return badges;
  }, [floorDefaults, choiceChildItems]);

  // Group floors by floor number for the tabs
  const floors = useMemo(() => {
    if (!currentProject || !allFloors) return [];
    return allFloors.sort((a, b) => a.floor_number - b.floor_number);
  }, [currentProject, allFloors]);

  const spaces = useMemo(() => {
    if (!currentProject?.id || !allSpaces) return [];
    return allSpaces;
  }, [currentProject?.id, allSpaces]);

  const windows = useMemo(() => {
    if (!currentProject?.id || !allWindows) return [];
    return allWindows;
  }, [currentProject?.id, allWindows]);

  // Memoized data transformations
  const sortedFloors = useMemo(() => {
    if (!floors) return [];
    return floors;
  }, [floors]);

  // Group windows by space for efficient lookup
  const windowsBySpace = useMemo(() => {
    if (!windows) return new Map();
    return new Map(
      windows.reduce((acc, window) => {
        const spaceId = window.space_id;
        if (!acc.has(spaceId)) acc.set(spaceId, []);
        acc.get(spaceId).push(window);
        return acc;
      }, new Map()),
    );
  }, [windows]);

  // Group spaces by floor with their windows
  const spacesByFloor = useMemo(() => {
    if (!spaces || !windowsBySpace) return new Map();

    return new Map(
      floors.map(floor => [
        floor.id,
        spaces
          .filter(space => space.floor_id === floor.id)
          .map(space => ({
            ...space,
            windows: windowsBySpace.get(space.id) || [],
          })),
      ]),
    );
  }, [spaces, windowsBySpace, floors]);

  const displayedFloors = useMemo(() => {
    if (!sortedFloors) return [];
    if (selectedFloor) {
      const selectedFloorData = sortedFloors.find(f => f.id === selectedFloor);
      return selectedFloorData ? [selectedFloorData] : [];
    }
    return sortedFloors;
  }, [sortedFloors, selectedFloor]);

  // Memoized handlers
  const handleAddFloor = useCallback(() => {
    openModal(MODAL_TYPES.FLOOR_FORM, {
      currentProject,
      allFloors: sortedFloors,
    });
  }, [openModal, currentProject, sortedFloors]);

  const handleEditFloor = useCallback(
    floor => {
      openModal(MODAL_TYPES.FLOOR_FORM, {
        floor,
        mode: 'edit',
        currentProject,
        allFloors: sortedFloors,
      });
    },
    [openModal, currentProject, sortedFloors],
  );

  const handleDeleteFloor = useCallback(
    async floorId => {
      const floorSpaces = spaces?.filter(space => space.floor_id === floorId) || [];

      if (floorSpaces.length > 0) {
        alert('לא ניתן למחוק קומה עם חללים קיימים. אנא מחק את החללים תחילה.');
        return;
      }

      if (!window.confirm('האם אתה בטוח שברצונך למחוק את הקומה?')) {
        return;
      }

      try {
        await removeFloor(floorId);
      } catch (error) {
        logError(error, {
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.DATA_DELETE,
          message: `Error deleting floor ${floorId}`,
        });
        alert('שגיאה במחיקת הקומה');
      }
    },
    [spaces, removeFloor],
  );

  const handleAddSpace = useCallback(
    floorId => {
      openModal(MODAL_TYPES.SPACE_FORM, {
        floorId,
        allFloors: sortedFloors,
      });
    },
    [openModal, sortedFloors],
  );

  const handleEditSpace = useCallback(
    space => {
      openModal(MODAL_TYPES.SPACE_FORM, {
        space,
        mode: 'edit',
        allFloors: sortedFloors,
      });
    },
    [openModal, sortedFloors],
  );

  const handleDeleteSpace = useCallback(
    async spaceId => {
      if (
        !window.confirm('האם אתה בטוח שברצונך למחוק את החלל? הפריטים שהוספת לא יימחקו לצמיתות.')
      ) {
        return;
      }

      try {
        await softDeleteSpace(spaceId);
      } catch (error) {
        logError(error, {
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.DATA_DELETE,
          message: `Error deleting space ${spaceId}`,
        });
        alert('שגיאה במחיקת החלל');
      }
    },
    [softDeleteSpace, logError],
  );

  const handleAnalyzeFloorPlan = useCallback(
    floor => {
      openModal(MODAL_TYPES.FLOOR_PLAN_ANALYSIS, {
        floorId: floor.id,
        floorName: formatBaseFloorName(floor.floor_number),
        projectId: currentProject?.id,
        onComplete: () => {
          // Data will be refreshed automatically via realtime subscriptions
        },
      });
    },
    [openModal, currentProject?.id],
  );

  // Error handling
  React.useEffect(() => {
    if (floorsError) {
      logError(floorsError, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_FETCH,
        message: `Error fetching floors for project ${currentProject?.id}`,
      });
    }
    if (spaceTypesError) {
      logError(spaceTypesError, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_FETCH,
        message: 'Error fetching space types from master data',
      });
    }
  }, [currentProject, logError, floorsError, spaceTypesError]);

  // Loading state
  const isLoading = floorsLoading || spacesLoading || windowsLoading || spaceTypesLoading;
  if (isLoading) {
    return (
      <div className={pageStyles.loadingContainer}>
        <LoadingSpinner />
      </div>
    );
  }

  // No project selected state
  if (!currentProject) {
    return <div className={pageStyles.emptyState}>בחר פרויקט כדי לצפות במבנה החללים</div>;
  }

  return (
    <div className={pageStyles.card}>
      {headerOverride || (
        <div className={pageStyles.cardHeader}>
          <h2 className={pageStyles.cardTitle}>החללים שלי</h2>
        </div>
      )}
      <div className={pageStyles.cardContent}>
        <FloorTabs
          floors={sortedFloors}
          selectedFloor={selectedFloor}
          onFloorSelect={setSelectedFloor}
          onAddFloor={handleAddFloor}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '20px' }}>
          {displayedFloors.map(floor => (
            <FloorSection
              key={floor.id}
              floor={floor}
              spaces={spacesByFloor.get(floor.id) || []}
              spaceTypes={spaceTypes}
              onDeleteSpace={handleDeleteSpace}
              onEditSpace={handleEditSpace}
              onDeleteFloor={handleDeleteFloor}
              onEditFloor={handleEditFloor}
              onAddSpace={handleAddSpace}
              onAnalyzeFloorPlan={handleAnalyzeFloorPlan}
              choiceBadges={floorChoiceBadges[floor.id] || []}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Individual space card component
const SpaceCard = React.memo(function SpaceCard({ space, spaceType, onDelete, onEdit }) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const spaceName = space.custom_space_name || spaceType?.name || 'חלל ללא שם';
  const roomSize = space.room_size;
  const roomLength = space.room_length;
  const roomWidth = space.room_width;
  const windowCount = space.windows?.length || 0;
  const spaceCost = space.space_cost;

  return (
    <div
      className={pageStyles.summaryCard}
      style={{ cursor: 'pointer' }}
      onClick={e => {
        if (e.target.closest('button')) return;
        navigate(`/spaces/${space.id}`);
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#1e293b', margin: 0 }}>
          {spaceName}
        </h4>
        <div
          style={{
            display: 'flex',
            gap: '4px',
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.15s ease',
          }}
        >
          <button
            onClick={() => onEdit(space)}
            className={tableStyles.editButton}
            style={{ opacity: 1 }}
            aria-label="ערוך"
          >
            <PencilSimple size={16} />
          </button>
          <button
            onClick={() => onDelete(space.id)}
            className={tableStyles.deleteButton}
            style={{ opacity: 1 }}
            aria-label="מחק"
          >
            <Trash size={16} />
          </button>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
        {space.custom_space_name && spaceType?.name && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 500,
              color: '#475569',
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              borderRadius: '20px',
              border: '1px solid #e2e8f0',
              boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.8)',
              letterSpacing: '0.01em',
            }}
          >
            {spaceType.name}
          </span>
        )}
        {roomSize && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 500,
              color: '#475569',
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              borderRadius: '20px',
              border: '1px solid #e2e8f0',
              boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.8)',
              letterSpacing: '0.01em',
            }}
          >
            שטח: {Number(roomSize).toFixed(2)} מ״ר
          </span>
        )}
        {roomLength && roomWidth && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 500,
              color: '#475569',
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              borderRadius: '20px',
              border: '1px solid #e2e8f0',
              boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.8)',
              letterSpacing: '0.01em',
            }}
          >
            מידות: {Number(roomLength).toFixed(2)} × {Number(roomWidth).toFixed(2)} מ׳
          </span>
        )}
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: 500,
            color: '#475569',
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            borderRadius: '20px',
            border: '1px solid #e2e8f0',
            boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.8)',
            letterSpacing: '0.01em',
          }}
        >
          חלונות: {windowCount}
        </span>
        {spaceCost > 0 && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 500,
              color: '#475569',
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              borderRadius: '20px',
              border: '1px solid #e2e8f0',
              boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.8)',
              letterSpacing: '0.01em',
            }}
          >
            עלות: {spaceCost} ₪
          </span>
        )}
      </div>
    </div>
  );
});

// Floor tabs component
const FloorTabs = React.memo(function FloorTabs({
  floors,
  selectedFloor,
  onFloorSelect,
  onAddFloor,
}) {
  const allOptions = useMemo(
    () => (floors?.length > 1 ? [ALL_FLOORS_OPTION, ...floors] : floors || []),
    [floors],
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div className={pageStyles.tabBar}>
        {allOptions.map(item => {
          const isActive =
            selectedFloor === item.id || (item.id === ALL_FLOORS_ID && selectedFloor === null);
          return (
            <button
              key={item.id}
              className={isActive ? pageStyles.tabActive : pageStyles.tab}
              onClick={() => onFloorSelect(item.id === ALL_FLOORS_ID ? null : item.id)}
            >
              {item.id === ALL_FLOORS_ID ? item.name : formatBaseFloorName(item.floor_number)}
            </button>
          );
        })}
      </div>
      <button className={pageStyles.secondaryButton} onClick={onAddFloor}>
        <Plus size={16} />
        <span>הוסף קומה</span>
      </button>
    </div>
  );
});

// Floor section component
const FloorSection = React.memo(function FloorSection({
  floor,
  spaces,
  spaceTypes,
  onDeleteSpace,
  onEditSpace,
  onDeleteFloor,
  onEditFloor,
  onAddSpace,
  onAnalyzeFloorPlan,
  choiceBadges = [],
}) {
  return (
    <div key={floor.id} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#f8fafc',
          padding: '12px 16px',
          borderRadius: '8px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b', margin: 0 }}>
            {formatBaseFloorName(floor.floor_number)}
          </h3>
          {floor.stairs_to_floor > 0 && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '3px 10px',
                fontSize: '12px',
                fontWeight: 500,
                color: '#64748b',
                background: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
              }}
            >
              {floor.stairs_to_floor} מדרגות
            </span>
          )}
          {choiceBadges.map((name, i) => (
            <span
              key={i}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '3px 10px',
                fontSize: '12px',
                fontWeight: 500,
                color: '#2563eb',
                background: '#eff6ff',
                borderRadius: '12px',
                border: '1px solid #bfdbfe',
              }}
            >
              {name}
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            onClick={() => onAnalyzeFloorPlan(floor)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              boxShadow: '0 1px 2px rgba(139, 92, 246, 0.2)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background =
                'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background =
                'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            title="נתח תוכנית קומה"
            aria-label="נתח תוכנית קומה"
          >
            <FileImage size={16} />
            <span>נתח תוכנית</span>
          </button>
          <button
            onClick={() => onEditFloor(floor)}
            className={tableStyles.editButton}
            style={{ opacity: 1 }}
            aria-label="ערוך קומה"
          >
            <PencilSimple size={16} />
          </button>
          <button
            onClick={() => onDeleteFloor(floor.id)}
            className={tableStyles.deleteButton}
            style={{ opacity: 1 }}
            title="מחק קומה"
            aria-label="מחק קומה"
          >
            <Trash size={16} />
          </button>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 250px))',
          gap: '16px',
        }}
      >
        {spaces.map(space => (
          <SpaceCard
            key={space.id}
            space={space}
            spaceType={spaceTypes?.find(t => t.id === space.master_space_id)}
            onDelete={onDeleteSpace}
            onEdit={onEditSpace}
          />
        ))}

        <div
          onClick={() => onAddSpace(floor.id)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '20px',
            background: '#ffffff',
            borderRadius: '12px',
            cursor: 'pointer',
            border: '2px dashed #e2e8f0',
            color: '#64748b',
            transition: 'all 0.15s ease',
            minHeight: '100px',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = '#2563eb';
            e.currentTarget.style.color = '#2563eb';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = '#e2e8f0';
            e.currentTarget.style.color = '#64748b';
          }}
        >
          <Plus size={24} />
          <span style={{ fontWeight: 500 }}>הוסף חלל חדש</span>
        </div>
      </div>
    </div>
  );
});

export default SpacesStructure;
